import {Helmet} from '@dr.pogodin/react-helmet';
import {skipToken} from '@reduxjs/toolkit/query';
import {Button, Card, DataStatus, openDialog, showNotification} from 'platform/components';
import {Box, Heading, HStack, Text, VStack} from 'platform/foundation';

import {useState} from 'react';

import {isNil, isNotNil} from 'ramda';

import {
  RecordInformationBody,
  useCreateRecordAuditMutation,
  useGetRecordAuditDefinitionQuery,
} from '@dms/api/core';
import {useGetCurrentUserInfoQuery} from '@dms/api/customer';
import {RecordIdHttpModel} from '@dms/api/shared';
import i18n from '@dms/i18n';
import {recordAuditRoutes} from '@dms/routes';
import {Main, Snippet, handleApiError, useBranches} from '@dms/shared';

import {ApiError, composePath, useNavigate, useOnMount, useQueryState} from 'shared/web';

import {AddParticipantDialog} from '../../components/AddParticipantDialog';
import {usePairedRecordSnippet} from '../../components/pairedRecord/usePairedRecordSnippet';
import {RecordAuditPairRecordDialog} from '../../components/RecordAuditPairRecordDialog';
import {isRecordAuditPairResourceId} from '../../components/recordAuditPairResources';
import {RecordAuditMemberEditDropdown} from '../../components/recordAuditParticipation/RecordAuditMemberEditDropdown';
import {
  RecordAuditParticipation,
  TeamParticipant,
} from '../../components/recordAuditParticipation/RecordAuditParticipation';
import {toCreateRecordAuditBody} from '../../utils/toCreateRecordAuditBody';

const RECORD_AUDIT_DEFINITION_QUERY_PARAM = 'recordAuditDefinitionId';
const RECORD_RESOURCE_ID_QUERY_PARAM = 'recordResourceId';
const RECORD_ID_QUERY_PARAM = 'recordId';

export function RecordAuditCreate() {
  const navigate = useNavigate();
  const [recordAuditDefinitionId] = useQueryState(RECORD_AUDIT_DEFINITION_QUERY_PARAM);
  const [recordResourceId] = useQueryState(RECORD_RESOURCE_ID_QUERY_PARAM);
  const [recordId] = useQueryState(RECORD_ID_QUERY_PARAM);

  const {data: userInfo, isLoading: isUserInfoLoading} = useGetCurrentUserInfoQuery();
  const definitionQuery = useGetRecordAuditDefinitionQuery(
    isNotNil(recordAuditDefinitionId) ? {definitionId: recordAuditDefinitionId} : skipToken
  );

  useOnMount(() => {
    if (isNil(recordAuditDefinitionId)) {
      navigate(recordAuditRoutes.overview, {replace: true});
    }
  });

  const prebindedRecord: RecordIdHttpModel | null =
    isNotNil(recordResourceId) && isNotNil(recordId)
      ? {resourceId: recordResourceId, id: recordId}
      : null;

  return (
    <>
      <Helmet title={i18n.t('module.checklistAudits.title')} />
      <Main
        heading={i18n.t('page.checklistAudits.create.title', {
          checklist: definitionQuery.data?.name ?? '',
        })}
      >
        <DataStatus isLoading={isUserInfoLoading} minHeight={100}>
          {isNotNil(recordAuditDefinitionId) ? (
            <RecordAuditCreateForm
              recordAuditDefinitionId={recordAuditDefinitionId}
              defaultAuditorId={userInfo?.id ?? ''}
              defaultBranchId={userInfo?.settings?.branch || userInfo?.defaultBranch?.id || ''}
              prebindedRecord={prebindedRecord}
            />
          ) : null}
        </DataStatus>
      </Main>
    </>
  );
}

interface RecordAuditCreateFormProps {
  recordAuditDefinitionId: string;
  defaultAuditorId: string;
  defaultBranchId: string;
  prebindedRecord: RecordIdHttpModel | null;
}

function RecordAuditCreateForm(props: RecordAuditCreateFormProps) {
  const navigate = useNavigate();
  const branchesQuery = useBranches();
  const [createAudit, createAuditQuery] = useCreateRecordAuditMutation();

  const [pickedRecord, setPickedRecord] = useState<RecordIdHttpModel | null>(props.prebindedRecord);
  const [recordInformation, setRecordInformation] = useState<RecordInformationBody | null>(null);
  const [auditorEmployeeId, setAuditorEmployeeId] = useState(props.defaultAuditorId);
  const [responsiblePersonEmployeeId, setResponsiblePersonEmployeeId] = useState<string | null>(
    null
  );
  const [branchId, setBranchId] = useState(props.defaultBranchId);
  const [participants, setParticipants] = useState<TeamParticipant[]>([]);

  const branchOptions = branchesQuery.branchOptions.map((option) => ({
    value: option.value,
    label: option.label ?? '',
  }));
  const branchName =
    branchOptions.find((option) => option.value === branchId)?.label ??
    i18n.t('general.notifications.notFound');

  const openPairDialog = () => {
    openDialog(
      <RecordAuditPairRecordDialog
        initialResourceId={
          isRecordAuditPairResourceId(pickedRecord?.resourceId)
            ? pickedRecord.resourceId
            : undefined
        }
        initialRecordInformation={recordInformation}
        onConfirm={(record) => {
          setPickedRecord(record);
          setRecordInformation(null);
        }}
        onConfirmCustom={(info) => {
          setRecordInformation(info);
          setPickedRecord(null);
        }}
      />,
      {title: i18n.t('page.checklistAudits.pairing.dialogTitle'), size: 'large'}
    );
  };

  const openAddParticipantDialog = () => {
    openDialog(
      <AddParticipantDialog
        existingEmployeeIds={participants.map((participant) => participant.employeeId)}
        onConfirm={(participant) => setParticipants((prev) => [...prev, participant])}
      />,
      {title: i18n.t('page.checklistAudits.create.actions.addParticipant'), size: 'large'}
    );
  };

  const {mainSnippet, vehicleSnippet} = usePairedRecordSnippet({
    record: pickedRecord,
    recordInformation,
    isEditable: true,
    onPair: openPairDialog,
  });

  const handleCreate = () => {
    createAudit({
      body: toCreateRecordAuditBody({
        values: {
          auditorEmployeeId,
          responsiblePersonEmployeeId,
          date: new Date(),
          branchId,
          participants,
        },
        recordAuditDefinitionId: props.recordAuditDefinitionId,
        pairedRecord: pickedRecord,
        recordInformation,
      }),
    })
      .unwrap()
      .then((response) => {
        showNotification.success(i18n.t('page.checklistAudits.notifications.created'));
        navigate(composePath(recordAuditRoutes.detail, {params: {id: response.id}}));
      })
      .catch((error: ApiError) => handleApiError(error));
  };

  const isCreateDisabled = auditorEmployeeId === '' || branchId === '';

  return (
    <Box paddingBottom={20} maxWidth={400}>
      <VStack spacing={6}>
        <Card title={i18n.t('page.checklistAudits.pairing.dialogTitle')}>
          <HStack spacing={4} wrap>
            <Box minWidth={80}>
              <Snippet {...mainSnippet} />
            </Box>
            {isNotNil(vehicleSnippet) ? (
              <Box minWidth={80}>
                <Snippet {...vehicleSnippet} />
              </Box>
            ) : null}
          </HStack>
        </Card>

        <Card>
          <HStack justify="space-between" align="center">
            <VStack spacing={1}>
              <Text size="xSmall" color="tertiary">
                {i18n.t('page.checklistAudits.create.fields.branch')}
              </Text>
              <Heading size={5}>{branchName}</Heading>
            </VStack>
            <RecordAuditMemberEditDropdown
              data-testid="recordAuditCreate-branch"
              options={branchOptions}
              selectedValue={branchId}
              isLoading={branchesQuery.isLoading}
              onSelect={setBranchId}
            />
          </HStack>
        </Card>

        <RecordAuditParticipation
          data-testid="recordAuditCreate"
          auditorEmployeeId={auditorEmployeeId}
          responsiblePersonEmployeeId={responsiblePersonEmployeeId}
          participants={participants}
          isEditable
          onAddParticipant={openAddParticipantDialog}
          onRemoveParticipant={(index) =>
            setParticipants((prev) => prev.filter((_, i) => i !== index))
          }
          onChangeAuditor={setAuditorEmployeeId}
          onChangeResponsiblePerson={setResponsiblePersonEmployeeId}
        />

        <HStack justify="flex-end">
          <Button
            variant="primary"
            title={i18n.t('page.checklistAudits.create.actions.submit')}
            onClick={handleCreate}
            isDisabled={isCreateDisabled}
            isLoading={createAuditQuery.isLoading}
            data-testid="recordAuditCreate-submit"
          />
        </HStack>
      </VStack>
    </Box>
  );
}
